import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  styled,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { type ReactNode, useId, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import {
  type WorkflowStepField,
  WorkflowStepFieldKind,
  type WorkflowStepFieldReference,
} from 'src/gql/graphql';
import {
  WorkflowNodeType,
  type WorkflowStepData,
  type WorkflowStepProblem,
} from 'src/pages/Settings/workflows/utils/workflow-definition';
import { getAtPath } from 'src/pages/Settings/workflows/utils/workflow-definition-edits';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';

export interface WorkflowStepFieldInputProps {
  nodeId: string;
  field: WorkflowStepField;
  /** The field's dot path inside the step's data — the same path a save error names. */
  path: string;
  /** The whole step's data; GROUP and condition fields read their children from it. */
  data: WorkflowStepData | undefined;
  /** This step's problems; the field shows the ones whose `field` is `path` (or under it). */
  problems: WorkflowStepProblem[];
  /** An active version or a platform workflow: values read as plain text, not inputs. */
  readOnly: boolean;
  onChange: (path: string, value: unknown) => void;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SIMPLE_LIST_ITEM_FIELD: Record<string, string> = { say: 'message', play: 'url' };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const RawValue = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
  fontSize: '0.75rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const ReadOnlyValue = ({ value }: { value: ReactNode }) =>
  value === '' || value === null || value === undefined ? (
    <Typography variant="body2" color="text.secondary">
      Not set
    </Typography>
  ) : (
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  );

type ScalarType = 'STRING' | 'NUMBER' | 'BOOLEAN';

const SCALAR_TYPE_LABEL: Record<ScalarType, string> = {
  STRING: 'Text',
  NUMBER: 'Number',
  BOOLEAN: 'True / false',
};

/** The type a stored SCALAR value already has; text when unset. */
const scalarTypeOf = (value: unknown, allowed: ScalarType[]): ScalarType => {
  let type: ScalarType = 'STRING';
  if (typeof value === 'number') type = 'NUMBER';
  if (typeof value === 'boolean') type = 'BOOLEAN';
  return allowed.includes(type) ? type : (allowed[0] ?? 'STRING');
};

/** `text` as a stored value of `type`; a number that doesn't parse yet stays text. */
const scalarFromText = (text: string, type: ScalarType): unknown => {
  if (text === '') return undefined;
  if (type === 'BOOLEAN') return text === 'true';
  if (type === 'NUMBER' && text.trim() !== '' && Number.isFinite(Number(text))) {
    return Number(text);
  }
  return text;
};

/**
 * A SCALAR value — text, a number, or true / false, from the types the field allows. The type is
 * chosen, not guessed from the text: the run compares with `===`, so "101" and 101 never match.
 * What's typed is kept as typed, so "1." and "+44" survive while the number is being written.
 */
const ScalarInput = ({
  value,
  valueTypes,
  inputId,
  error,
  onChange,
}: {
  value: unknown;
  valueTypes: string[];
  inputId: string;
  error: boolean;
  onChange: (value: unknown) => void;
}) => {
  const allowed = (['STRING', 'NUMBER', 'BOOLEAN'] as const).filter((type) =>
    valueTypes.includes(type),
  );
  const types: ScalarType[] = allowed.length ? [...allowed] : ['STRING'];
  const asText = (stored: unknown) =>
    stored === undefined || stored === null ? '' : String(stored);
  const [type, setType] = useState<ScalarType>(() => scalarTypeOf(value, types));
  // the text as typed, for as long as it still means the stored value
  const [draft, setDraft] = useState({ text: asText(value), value });
  let text = draft.text;
  if (draft.value !== value) {
    // changed elsewhere (another edit, a discard): show what's stored, as its own type
    text = asText(value);
    setDraft({ text, value });
    if (value !== undefined) setType(scalarTypeOf(value, types));
  }

  const write = (nextText: string, nextType: ScalarType) => {
    const next = scalarFromText(nextText, nextType);
    setDraft({ text: nextText, value: next });
    onChange(next);
  };

  return (
    <Stack direction="row" spacing={0.5}>
      {types.length > 1 ? (
        <Select
          size="small"
          value={type}
          inputProps={{ 'aria-label': 'Value type' }}
          onChange={(event) => {
            const nextType = event.target.value as ScalarType;
            setType(nextType);
            write(nextType === 'BOOLEAN' ? String(text === 'true') : text, nextType);
          }}
          sx={{ flexShrink: 0 }}
        >
          {types.map((option) => (
            <MenuItem key={option} value={option}>
              {SCALAR_TYPE_LABEL[option]}
            </MenuItem>
          ))}
        </Select>
      ) : null}
      {type === 'BOOLEAN' ? (
        <Select
          id={inputId}
          size="small"
          fullWidth
          value={value === true ? 'true' : 'false'}
          error={error}
          onChange={(event) => write(String(event.target.value), 'BOOLEAN')}
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </Select>
      ) : (
        <TextField
          id={inputId}
          size="small"
          fullWidth
          value={text}
          error={error}
          inputProps={type === 'NUMBER' ? { inputMode: 'decimal' } : undefined}
          onChange={(event) => write(event.target.value, type)}
        />
      )}
    </Stack>
  );
};

/**
 * Key / value rows. The rows are kept here, in order, while they're edited; the step gets the
 * object only when every name is filled in and used once, so typing a name that passes through an
 * existing one never merges the two rows.
 */
const KeyValueRows = ({
  field,
  value,
  onChange,
}: {
  field: WorkflowStepField;
  value: unknown;
  onChange: (value: unknown) => void;
}) => {
  const stored = isPlainObject(value) ? value : {};
  const toRows = (object: Record<string, unknown>): [string, string][] =>
    Object.entries(object).map(([name, entryValue]) => [name, String(entryValue ?? '')]);
  const [state, setState] = useState(() => ({ rows: toRows(stored), value }));
  let { rows } = state;
  if (state.value !== value) {
    // changed elsewhere (another edit, a discard): start again from what's stored
    rows = toRows(stored);
    setState({ rows, value });
  }

  const names = rows.map(([name]) => name).filter(Boolean);
  const duplicate = names.find((name, index) => names.indexOf(name) !== index);

  const update = (next: [string, string][]) => {
    const filled = next.filter(([name]) => name !== '');
    const unique = new Set(filled.map(([name]) => name)).size === filled.length;
    const nextValue = unique ? Object.fromEntries(filled) : value;
    setState({ rows: next, value: nextValue });
    if (unique) onChange(nextValue);
  };

  return (
    <>
      {rows.map(([name, entryValue], index) => (
        // eslint-disable-next-line react/no-array-index-key -- rows have no id of their own, and the name is the value being edited
        <Stack key={index} direction="row" spacing={0.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Name"
            value={name}
            error={!!name && name === duplicate}
            inputProps={{ 'aria-label': `${field.label} name` }}
            onChange={(event) =>
              update(rows.map((row, i) => (i === index ? [event.target.value, row[1]] : row)))
            }
          />
          <TextField
            size="small"
            placeholder="Value"
            value={entryValue}
            inputProps={{ 'aria-label': `${field.label} value` }}
            onChange={(event) =>
              update(rows.map((row, i) => (i === index ? [row[0], event.target.value] : row)))
            }
          />
          <IconButton
            size="small"
            aria-label={`Remove ${name || 'row'}`}
            onClick={() => update(rows.filter((_, i) => i !== index))}
          >
            <DynamicIcon name="close" fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      {duplicate ? (
        <FormHelperText error sx={{ mx: 0 }}>
          “{duplicate}” is used twice. Each name can be used once.
        </FormHelperText>
      ) : null}
      <Button
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        disabled={rows.some(([name]) => name === '')}
        onClick={() => update([...rows, ['', '']])}
      >
        Add a row
      </Button>
    </>
  );
};

/** "On" / "Off" for a stored boolean, nothing for a setting left unset. */
const onOffText = (value: unknown) => {
  if (value === true) return 'On';
  if (value === false) return 'Off';
  return null;
};

const toTimeText = (hour: unknown, minute: unknown) =>
  `${String(hour ?? 0).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;

/** "09:30" as hour and minute; `null` while the time input is incomplete or cleared. */
const fromTimeText = (text: string) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(text);
  return match ? { hour: Number(match[1]), minute: Number(match[2]) } : null;
};

/** Problems for this field: its own, and for list-like kinds the ones about its items too. */
const problemsAt = (problems: WorkflowStepProblem[], path: string, includeItems: boolean) =>
  problems.filter(
    ({ field }) =>
      field === path ||
      (includeItems &&
        !!field &&
        (field.startsWith(`${path}.`) || field.startsWith(`${path}[`))),
  );

/** "From <step>" for a field wired from an earlier step's output. */
const ReferenceValue = ({
  value,
  reference,
}: {
  value: unknown;
  reference: WorkflowStepFieldReference;
}) => {
  const { definition, catalog } = useWorkflowEditor();
  const sourceId =
    typeof value === 'string' ? /^\{\{state\.([^.}]+)\./.exec(value)?.[1] : undefined;
  const source = definition.nodes.find(({ id }) => id === sourceId);

  // Not an error: the field fills itself once a providing step is above this one.
  if (!value) {
    const providers = reference.stepTypes
      .map((type) => catalog.byType.get(type)?.label ?? type)
      .join(' or ');
    return (
      <Typography variant="body2" color="text.secondary">
        Filled in from a {providers} step above this one. There isn’t one yet.
      </Typography>
    );
  }
  if (!source) return <ReadOnlyValue value={String(value)} />;

  return (
    <Chip
      size="small"
      icon={<DynamicIcon name="link" fontSize="small" />}
      label={`From ${source.name || (catalog.byType.get(source.type)?.label ?? source.id)}`}
      sx={{ alignSelf: 'flex-start' }}
    />
  );
};

/**
 * One field of a step, drawn by its catalog `kind`: text and long text for TEMPLATE, a select for
 * ENUM, a switch for BOOLEAN, rows for KEY_VALUE and TIME_RANGES, nested fields for GROUP and for
 * the condition behind a BOOLEAN_OR_CONDITION switch. A field with a `reference` is filled from an
 * earlier step and shows as "From <step>" rather than an input.
 *
 * STEP_LIST items that aren't plain say / play read as values with "Edit in JSON" — the hold-menu
 * depth phase 1 leaves out.
 */
export const WorkflowStepFieldInput = (props: WorkflowStepFieldInputProps) => {
  const { field, path, data, problems, readOnly, onChange } = props;
  const inputId = useId();
  const labelId = `${inputId}-label`;
  const value = getAtPath(data, path);

  const listLike =
    field.kind === WorkflowStepFieldKind.StepList ||
    field.kind === WorkflowStepFieldKind.KeyValue ||
    field.kind === WorkflowStepFieldKind.TimeRanges;
  const fieldProblems = problemsAt(problems, path, listLike);
  const hasError = fieldProblems.length > 0;

  const label = (
    <FormFieldLabel id={labelId} htmlFor={inputId} error={hasError}>
      {field.label}
      {!field.required && !field.requiredUnless ? (
        <Typography component="span" variant="caption" color="text.secondary">
          {' '}
          (optional)
        </Typography>
      ) : null}
    </FormFieldLabel>
  );

  const helper = (
    <>
      {fieldProblems.map((problem) => (
        <FormHelperText key={`${problem.field}:${problem.message}`} error sx={{ mx: 0 }}>
          {problem.message}
        </FormHelperText>
      ))}
      {field.description && !readOnly ? (
        <FormHelperText sx={{ mx: 0 }}>{field.description}</FormHelperText>
      ) : null}
    </>
  );

  const nested = (fields: WorkflowStepField[]) => (
    <Stack spacing={1.5} sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
      {fields.map((child) => (
        <WorkflowStepFieldInput
          key={child.key}
          {...props}
          field={child}
          path={`${path}.${child.key}`}
        />
      ))}
    </Stack>
  );

  if (field.reference) {
    return (
      <Stack>
        {label}
        <ReferenceValue value={value} reference={field.reference} />
        {helper}
      </Stack>
    );
  }

  switch (field.kind) {
    case WorkflowStepFieldKind.Template:
    case WorkflowStepFieldKind.Path:
    case WorkflowStepFieldKind.Scalar:
    case WorkflowStepFieldKind.Number: {
      const text = value === undefined || value === null ? '' : String(value);
      if (readOnly) {
        return (
          <Stack>
            {label}
            <ReadOnlyValue value={text} />
          </Stack>
        );
      }

      if (field.kind === WorkflowStepFieldKind.Scalar) {
        return (
          <Stack>
            {label}
            <ScalarInput
              value={value}
              valueTypes={field.valueTypes ?? []}
              inputId={inputId}
              error={hasError}
              onChange={(next) => onChange(path, next)}
            />
            {helper}
          </Stack>
        );
      }

      const toValue = (next: string): unknown => {
        if (field.kind === WorkflowStepFieldKind.Number) {
          return next === '' ? undefined : Number(next);
        }
        return next;
      };

      return (
        <Stack>
          {label}
          <TextField
            id={inputId}
            size="small"
            fullWidth
            value={text}
            error={hasError}
            type={field.kind === WorkflowStepFieldKind.Number ? 'number' : 'text'}
            multiline={field.kind === WorkflowStepFieldKind.Template}
            maxRows={6}
            onChange={(event) => onChange(path, toValue(event.target.value))}
          />
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.Enum: {
      const options = field.options ?? [];
      const selected = typeof value === 'string' ? value : '';
      if (readOnly) {
        return (
          <Stack>
            {label}
            <ReadOnlyValue
              value={options.find((option) => option.value === selected)?.label ?? selected}
            />
          </Stack>
        );
      }

      return (
        <Stack>
          {label}
          <Select
            id={inputId}
            size="small"
            fullWidth
            value={selected}
            error={hasError}
            displayEmpty
            // name the combobox by the label alone, not label plus the picked value
            SelectDisplayProps={{ 'aria-labelledby': labelId } as object}
            onChange={(event) => onChange(path, event.target.value || undefined)}
          >
            {field.required ? null : (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.Boolean: {
      if (readOnly) {
        return (
          <Stack>
            {label}
            <ReadOnlyValue
              value={onOffText(typeof value === 'boolean' ? value : !!field.defaultOn)}
            />
          </Stack>
        );
      }

      return (
        <Stack>
          <FormControlLabel
            label={field.label}
            labelPlacement="start"
            sx={{ mx: 0, justifyContent: 'space-between' }}
            control={
              <Switch
                // unset runs as the catalog's default, so it shows as that
                checked={typeof value === 'boolean' ? value : !!field.defaultOn}
                onChange={(_, checked) => onChange(path, checked)}
              />
            }
          />
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.BooleanOrCondition: {
      const isCondition = isPlainObject(value);
      // unset runs as the catalog's default
      const isOn = isCondition || (typeof value === 'boolean' ? value : !!field.defaultOn);
      if (readOnly) {
        return (
          <Stack spacing={1}>
            {label}
            {isCondition ? (
              nested(field.fields ?? [])
            ) : (
              <ReadOnlyValue value={onOffText(isOn)} />
            )}
          </Stack>
        );
      }

      return (
        <Stack spacing={1}>
          <FormControlLabel
            label={field.label}
            labelPlacement="start"
            sx={{ mx: 0, justifyContent: 'space-between' }}
            control={
              <Switch checked={isOn} onChange={(_, checked) => onChange(path, checked)} />
            }
          />
          {isOn ? (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => onChange(path, isCondition ? true : {})}
            >
              {isCondition ? 'Always' : 'Only when…'}
            </Button>
          ) : null}
          {isCondition ? nested(field.fields ?? []) : null}
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.Group: {
      if (!isPlainObject(value)) {
        return (
          <Stack>
            {label}
            {readOnly ? (
              <ReadOnlyValue value={null} />
            ) : (
              <Button
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => onChange(path, {})}
              >
                Add {field.label.toLowerCase()}
              </Button>
            )}
            {helper}
          </Stack>
        );
      }

      return (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {label}
            {readOnly || field.required ? null : (
              <Button size="small" color="error" onClick={() => onChange(path, undefined)}>
                Remove
              </Button>
            )}
          </Stack>
          {nested(field.fields ?? [])}
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.KeyValue: {
      const entries = Object.entries(isPlainObject(value) ? value : {});
      if (readOnly) {
        return (
          <Stack spacing={1}>
            {label}
            {entries.length === 0 ? <ReadOnlyValue value={null} /> : null}
            {entries.map(([name, entryValue]) => (
              <Typography key={name} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {name} = {String(entryValue)}
              </Typography>
            ))}
            {helper}
          </Stack>
        );
      }

      return (
        <Stack spacing={1}>
          {label}
          <KeyValueRows
            field={field}
            value={value}
            onChange={(next) => onChange(path, next)}
          />
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.TimeRanges: {
      const ranges = (Array.isArray(value) ? value : []) as Record<string, unknown>[];
      const write = (next: Record<string, unknown>[]) => onChange(path, next);
      const update = (index: number, patch: Record<string, unknown>) =>
        write(ranges.map((range, i) => (i === index ? { ...range, ...patch } : range)));

      return (
        <Stack spacing={1}>
          {label}
          {ranges.length === 0 && readOnly ? <ReadOnlyValue value={null} /> : null}
          {ranges.map((range, index) => {
            const start = toTimeText(range.startHour, range.startMinute);
            const end = toTimeText(range.endHour, range.endMinute);
            const day = DAYS[Number(range.day)] ?? String(range.day);

            return readOnly ? (
              // eslint-disable-next-line react/no-array-index-key -- stored time ranges carry no id
              <Typography key={index} variant="body2">
                {day} {start}–{end}
              </Typography>
            ) : (
              // eslint-disable-next-line react/no-array-index-key -- stored time ranges carry no id
              <Stack key={index} direction="row" spacing={0.5} alignItems="center">
                <Select
                  size="small"
                  value={Number(range.day) || 0}
                  inputProps={{ 'aria-label': 'Day' }}
                  onChange={(event) => update(index, { day: Number(event.target.value) })}
                >
                  {DAYS.map((name, dayIndex) => (
                    <MenuItem key={name} value={dayIndex}>
                      {name.slice(0, 3)}
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  size="small"
                  type="time"
                  value={start}
                  inputProps={{ 'aria-label': 'From' }}
                  onChange={(event) => {
                    // an incomplete time keeps what's stored until it's whole again
                    const time = fromTimeText(event.target.value);
                    if (time) {
                      update(index, { startHour: time.hour, startMinute: time.minute });
                    }
                  }}
                />
                <TextField
                  size="small"
                  type="time"
                  value={end}
                  inputProps={{ 'aria-label': 'To' }}
                  onChange={(event) => {
                    const time = fromTimeText(event.target.value);
                    if (time) update(index, { endHour: time.hour, endMinute: time.minute });
                  }}
                />
                <IconButton
                  size="small"
                  aria-label="Remove time range"
                  onClick={() => write(ranges.filter((_, i) => i !== index))}
                >
                  <DynamicIcon name="close" fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
          {readOnly ? null : (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() =>
                write([
                  ...ranges,
                  { day: 1, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
                ])
              }
            >
              Add a time range
            </Button>
          )}
          {helper}
        </Stack>
      );
    }

    case WorkflowStepFieldKind.StepList: {
      const items = (Array.isArray(value) ? value : []) as Record<string, unknown>[];
      const allowed = (field.allowedStepTypes ?? []).filter(
        (type) => SIMPLE_LIST_ITEM_FIELD[type],
      );
      // a say or play with only its text is editable here; anything richer stays as stored
      const isSimple = items.every((item) => {
        const textKey = SIMPLE_LIST_ITEM_FIELD[String(item.type)];
        const itemData = isPlainObject(item.data) ? item.data : {};
        return !!textKey && Object.keys(itemData).every((key) => key === textKey);
      });

      if (readOnly || !isSimple) {
        return (
          <Stack spacing={0.5}>
            {label}
            {items.length === 0 ? (
              <ReadOnlyValue value={null} />
            ) : (
              <RawValue>{JSON.stringify(items, null, 2)}</RawValue>
            )}
            {!readOnly ? (
              <Typography variant="caption" color="text.secondary">
                Edit in JSON — the canvas edits plain Say and Play items only.
              </Typography>
            ) : null}
            {helper}
          </Stack>
        );
      }

      const write = (next: Record<string, unknown>[]) => onChange(path, next);

      return (
        <Stack spacing={1}>
          {label}
          {items.map((item, index) => {
            const type = String(item.type);
            const textKey = SIMPLE_LIST_ITEM_FIELD[type];
            const itemData = isPlainObject(item.data) ? item.data : {};

            return (
              // eslint-disable-next-line react/no-array-index-key -- embedded wait-menu items may carry no id
              <Stack key={index} spacing={0.5}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Select
                    size="small"
                    value={type}
                    inputProps={{ 'aria-label': 'Item type' }}
                    onChange={(event) =>
                      write(
                        items.map((candidate, i) =>
                          i === index
                            ? { ...candidate, type: event.target.value, data: {} }
                            : candidate,
                        ),
                      )
                    }
                  >
                    {allowed.map((allowedType) => (
                      <MenuItem key={allowedType} value={allowedType}>
                        {allowedType === WorkflowNodeType.Say ? 'Say' : 'Play'}
                      </MenuItem>
                    ))}
                  </Select>
                  <Box flex={1} />
                  <IconButton
                    size="small"
                    aria-label="Remove item"
                    onClick={() => write(items.filter((_, i) => i !== index))}
                  >
                    <DynamicIcon name="close" fontSize="small" />
                  </IconButton>
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder={type === WorkflowNodeType.Say ? 'Message' : 'Audio URL'}
                  value={String(itemData[textKey] ?? '')}
                  onChange={(event) =>
                    write(
                      items.map((candidate, i) =>
                        i === index
                          ? { ...candidate, data: { [textKey]: event.target.value } }
                          : candidate,
                      ),
                    )
                  }
                />
              </Stack>
            );
          })}
          <Button
            size="small"
            sx={{ alignSelf: 'flex-start' }}
            disabled={!allowed.length}
            onClick={() => {
              const type = allowed[0];
              let n = items.length + 1;
              while (items.some((item) => item.id === `${type}-${n}`)) n += 1;
              write([...items, { id: `${type}-${n}`, type, data: {} }]);
            }}
          >
            Add an item
          </Button>
          {helper}
        </Stack>
      );
    }
  }
};
